import { useMemo } from 'react';
import { Product, ProductType, useCatalog } from 'queries';
import { useTranslation } from 'react-i18next';
import { CreatableAutoComplete, CreatableAutoCompleteProps } from '@app/form';
import { toEuros } from 'i18n';
import { getUnitProductPrice } from 'lib/priceComputation';

export interface ProductOption {
  label: string;
  value: string;
  __isNew__?: boolean;
  details?: Product;
}

interface Props
  extends Omit<
    CreatableAutoCompleteProps<ProductOption, false>,
    'isMulti' | 'options'
  > {
  productType?: ProductType;
  duration?: number;
  incentive?: number;
  pricePerParticipant?: boolean;
  locale?: string;
}
export const ProductAutocomple = ({
  productType: productTypeProps,
  pricePerParticipant = false,
  duration,
  incentive,
  locale,
  ...rest
}: Props) => {
  const { i18n } = useTranslation();

  const { data: catalog } = useCatalog(locale);
  const products = useMemo(() => {
    if (!catalog) {
      return [];
    }
    return catalog
      .filter(
        (p) =>
          p.type === productTypeProps &&
          p.pricePerParticipant === pricePerParticipant,
      )
      .sort((a, b) => a.price - b.price);
  }, [catalog, pricePerParticipant, productTypeProps]);

  const options = useMemo(() => {
    return products.map((product) => ({
      label: `${product.title || product.name} ${toEuros(
        getUnitProductPrice(product, duration, incentive),
      )}`,
      value: product.id,
      details: product,
    }));
  }, [products, i18n, duration, incentive]);

  return (
    <CreatableAutoComplete
      noMargin
      {...rest}
      options={options}
      isMulti={false}
    />
  );
};
